import * as React from 'react';
import Table, { TableProps } from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { SxProps } from '@mui/system';
import colors from 'src/utils/colors';

export interface Column<T> {
  id: string;
  header: string | React.ReactNode;
  renderCell: (row: T, index?: number) => React.ReactNode; // Changed signature
  minWidth?: string | number;
}

export interface BeaconProTableProps<T> {
  columns: Column<T>[];
  data?: T[];
  isLoading?: boolean;
  loadingRowCount?: number;
  page?: number;
  itemsPerPage?: number;
  tableSx?: SxProps<TableProps>;
}

/**
 * Simple presentational table component that is commonly used on Beacon Pro pages.
 * If you need more advanced features or specific functionality, consider composing your own variation of this component instead of adding more props.
 */
export function BeaconProTable<T>({
  columns,
  data = [],
  isLoading = false,
  loadingRowCount = 20,
  page = 1,
  itemsPerPage = 20,
  tableSx
}: BeaconProTableProps<T>) {
  const loadingRows = React.useMemo(() => Array(loadingRowCount).fill(null), [loadingRowCount]);

  const paginatedData = React.useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  }, [data, page, itemsPerPage]);

  return (
    <TableContainer
      sx={{
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          borderCollapse: 'unset',
          '& tbody .MuiTableCell-root': {
            padding: '12px 16px',
            height: '60px', // Add fixed height to all cells
            paddingRight: '0px'
          },
          '& thead .MuiTableCell-root': {
            padding: '0px 0px 16px 16px'
          },
          '& tbody td': {
            borderBottom: '1px solid #f0f0f0' // Move border bottom here
          },
          '& thead th': {
            border: 'none' // Explicitly remove borders from header cells
          },
          '& tbody td:first-of-type': {
            borderLeft: '1px solid #f0f0f0'
          },
          '& tbody td:last-child': {
            borderRight: '1px solid #f0f0f0'
          },
          '& tbody tr:first-of-type td': {
            borderTop: '1px solid #f0f0f0'
          },
          '& tbody tr:first-of-type td:first-of-type': {
            borderTopLeftRadius: '8px'
          },
          '& tbody tr:first-of-type td:last-of-type': {
            borderTopRightRadius: '8px'
          },
          '& tbody tr:first-of-type td:last-child': {
            borderTopRightRadius: '8px'
          },
          '& tbody tr:last-child td:first-of-type': {
            borderBottomLeftRadius: '8px'
          },
          '& tbody tr:last-child td:last-child': {
            borderBottomRightRadius: '8px'
          },
          '& thead .MuiTableCell-root span': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            fontWeight: 700,
            lineHeight: '20px',
            letterSpacing: '1.2px',
            textAlign: 'left',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            color: colors.darkBlue,
            textTransform: 'uppercase'
          },
          ...tableSx
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id} sx={{ minWidth: column.minWidth }}>
                <span>{column.header}</span>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? loadingRows.map((_, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      <SlSkeleton
                        variant="rounded"
                        sx={{
                          width: '100%',
                          height: '16px'
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : paginatedData?.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.id}>{column.renderCell(row)}</TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
